<template>

<div v-if="visible" :class="['amf-form-row', { expanded: expanded, highlighted: highlighted && expanded }]">
    <div :class="['container-fluid', 'top-border']" @click="click" style="cursor: pointer">

        <div class="row py-2">
            <div class="title col-2">
                <span>{{label}}</span>
            </div>

            <div class="text col-10">
                <div>
                    <span class="row-summary">{{summary}}</span>
                    <span class="pull-right" v-if="!expandedByParent">
                        <div v-if="!expanded" class="fa fa-chevron-down"></div>
                        <div v-if="expanded && !changesPending" class="fa fa-chevron-up"></div>
                    </span>
                </div>
            </div>
    </div>
    </div>

    <div class="container-fluid" v-if="expanded">
        <div class="row py-2">
            <div class="col-2">
            </div>
            <div class="col-10">
                <div>
                    <div class="row row-editor-container">
                        <slot></slot>
                        <div v-if="error" class="col-12 mt-2">
                            <error-message/>
                        </div>
                    </div>
                    <div v-if="explanation" class="explanation">
                        <span v-html="explanation"></span>
                    </div>
                    <div v-if="!readOnly && !expandedByParent && buttons == 'Save/Cancel'" class="row mb-2">
                        <div class="col-12">
                            <div class="pull-right">
                                <amf-button class="mr-1 btn-success" :onClick="save">Save</amf-button>
                                <amf-button class="btn-default" :onClick="cancel">Cancel</amf-button>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="(readOnly && !expandedByParent) || buttons == 'Close'" class="row mb-2">
                        <div class="col-12">
                            <div class="pull-right">
                                <a
                                    href="#"
                                    class="mr-2"
                                    v-on:click.prevent="addCollectionItem"
                                    v-if="addItem"
                                >
                                  <span :class="['fa', 'fa-plus', 'mr-1']"></span>
                                  {{ addItem.text }}
                                </a>
                                <amf-button class="btn-default" :onClick="cancel">Close</amf-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import AmfButton from './AmfButton';
import changeTracker from "../utils/change-tracker"
import { v } from "framework";

export default {
    components: { AmfButton },
    inject: ['closeRows'],
    props: {
        onSave: Function,
        onCancel: Function,
        onEdit: Function,
        label: { type: String, required: true },
        viewSummary: { type: String, required: true },
        editSummary: { type: String, required: false },
        hideEditSummary: { type: Boolean, required: false },
        explanation: String,
        visible: { type: Boolean, required: false, default: true },
        buttons: { type: String, default: 'Save/Cancel' },
        addItem: { type: Object, default: function () { return null; } },
    },
    data() {
        return {
            error: null,
            editing: false,
            highlighted: false,
            ready: false,
            showLabel: true
        }
    },
    provide: function() {
        return {
            closeEditor: this.closeEditor,
            refresh: this.refresh
        }
    },
    computed: {
        ...amfx.map("changeTracker", "changesPending"),
        ...amfx.map("guidance", "guidanceMessages: messages"),
        expanded() {
            return this.editing || this.expandedByParent;
        },
        readOnly() { 
            return this.getParentProperty("readOnly", false) || this.expandedByParent; 
        },
        summary() {
            if (!this.ready) return "...";
            if (!this.expanded) return this.viewSummary;
            if (this.expanded) return this.hideEditSummary ? "" : (this.editSummary || this.viewSummary);
            return this.viewSummary;
        },
        expandedByParent() {
            return this.getParentProperty("allRowsExpanded", false)
        },
    },
    async created() {
        await this.refresh();
    },
    methods: {
        addCollectionItem () {
            this.$parent.$parent.showModal(this.addItem);        
        },
        async refresh() {
            try {
                this.ready = false;
                if (this.$parent.load) await this.$parent.load();
                this.ready = true;
            } catch(err) {
                this.error = err;
            }
        },
        async click() {
            if (!this.ready) return;
            if(!this.editing) {
                await changeTracker.confirmNavigation();
                this.closeRows();
                this.openEditor();
            } else {
                if (!this.changesPending) {
                    this.closeEditor();
                }
            }
        },
        openEditor() {
            this.error = null;
            if (v.resetComponent(this)) return;
            if (this.onEdit) this.onEdit();
            this.editing = true;
        },
        closeEditor() {
            changeTracker.clear();
            this.editing = false;
            this.highlighted = false;
            this.error = null;
        },
        async save() {
            if (v.invalidComponent(this)) return;

            if(this.invalidParent()) return;

            if(this.onSave) {
                try {
                    await this.onSave();
                    this.closeEditor();
                } catch(err) {
                    this.error = err;
                }
            }
        },
        cancel() {
            if(this.onCancel) this.onCancel();
            this.closeEditor();
        },
        invalidParent(){
            if(this.$parent.validate){
                this.error = this.$parent.validate();
                if(this.error) return true;
            }
            return false;
        }
    },
    watch: {
        editing() {
            if (!this.editing) this.highlighted = false;
        },
        guidanceMessages: {
            immediate: true,
            handler() {
                var msgs = this.guidanceMessages;
                if (msgs.length == 0) return;
                let msg = _.last(msgs);
                if (msg.type == "highlight-row" && this.label == msg.row && this.$route.fullPath.startsWith(msg.url)) {
                    this.highlighted = true;
                    this.openEditor();
                } else if (msg.type == "clear-highlight") {
                    this.highlighted = false;
                    this.editing = false;
                }
            }
        },
    }
}


</script>

<style scoped lang="scss">
@import '../../scss/amf-variables.scss';

.amf-form-row {

    &.expanded {
        background-color: rgba(0,0,0,.03);
    }
    &.highlighted {
        border-color: $guidanceBorderColor;
        border-width: 2px;
        border-style: solid
    }

    .title {
        font-weight: 500;
        text-align: right;
    }

    .summary {
        color: gray;
    }

    a.amf-form-row-link {
        color: lighten(black, 20);
        font-size: 80%;
    }

    .explanation {
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
        font-size: 80%;
        color: lighten(black, 50);
    }
}

</style>



// WEBPACK FOOTER //
// src/framework/primitives/AmfFormRow.vue